// File: src/hooks/useLazyBackground.js
import { useState, useEffect } from 'react';

/**
 * Lazily "arms" a background image once the section it belongs to is
 * roughly one viewport away from entering view, rather than paying for
 * every section's background image on initial page load.
 *
 * @param {React.RefObject} sectionRef - ref already attached to the section
 *   element (the same ref the section uses for its own scroll-parallax
 *   effect).
 * @param {string} imageUrl - the background image URL to load in for this
 *   section. Not used directly by the hook (the caller builds the style
 *   object), but included so the hook can be a drop-in replacement if that
 *   ever changes.
 * @returns {boolean} true once the section has been observed entering the
 *   rootMargin threshold and the background image should be applied.
 */
const useLazyBackground = (sectionRef, imageUrl) => {
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || !imageUrl) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setBgLoaded(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '100% 0px', threshold: 0 }
    );

    observer.observe(node);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  return bgLoaded;
};

export default useLazyBackground;
